import React from 'react';
import CloseIcon from '@gumtree/ui-library-v2/src/icons/icon-set/medium/close.svg';
import { qaAttribute } from '../utils/qa-service';
import {
    StyledSubmitResultDialog,
    ImageContainer,
    Title,
    Description,
    ActionButton,
    ButtonsContainer,
} from './submit-result-dialog.style';

export interface SubmitResultDialogProps {
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Function called when dialog should be closed */
    onClose: () => void;
    /** Custom function called when close button is clicked (dialog will still close) */
    onCloseButtonClick?: () => void;
    /** Image source for the top image */
    imageSrc: string;
    /** Alt text for the image */
    imageAlt?: string;
    /** Bold title text */
    title: string;
    /** Regular description text */
    description: string;
    /** Text for the action button */
    buttonText: string;
    /** Function called when action button is clicked */
    onButtonClick?: () => void;
    /** QA attribute prefix for testing */
    qaAttributePrefix?: string;
    /** Whether the button is disabled */
    buttonDisabled?: boolean;
    /** Whether the dialog is in result mode */
    resultMode?: boolean;
    /** Text for the extra button */
    extraButtonText?: string;
    /** Whether the extra button is disabled */
    extraButtonDisabled?: boolean;
    /** Function called when extra button is clicked */
    onExtraButtonClick?: () => void;
}

const SubmitResultDialog: React.FC<SubmitResultDialogProps> = ({
    isOpen,
    onClose,
    onCloseButtonClick,
    imageSrc,
    imageAlt = '',
    title,
    description,
    buttonText,
    onButtonClick,
    qaAttributePrefix = 'submit-result-dialog',
    buttonDisabled = false,
    resultMode = true,
    extraButtonText,
    extraButtonDisabled,
    onExtraButtonClick,
}) => {
    const handleCloseButtonClick = () => {
        // Call custom close handler if provided
        if (onCloseButtonClick) {
            onCloseButtonClick();
        }
        // Always close the dialog
        onClose();
    };

    return (
        <StyledSubmitResultDialog
            isOpen={isOpen}
            onClose={onClose}
            hideCloseBtn // We'll use our custom close button
            {...qaAttribute(`${qaAttributePrefix}-modal`)}
            displayAsSheetOnMweb
        >
            {/* Custom close button */}
            <button
                type="button"
                className="dialog-close"
                aria-label="Close"
                onClick={handleCloseButtonClick}
                {...qaAttribute(`${qaAttributePrefix}-close-button`)}
            >
                <CloseIcon />
            </button>

            {/* Content */}
            <ImageContainer {...qaAttribute(`${qaAttributePrefix}-image-container`)}>
                <img src={imageSrc} alt={imageAlt} />
            </ImageContainer>

            <Title {...qaAttribute(`${qaAttributePrefix}-title`)}>
                {title}
            </Title>

            <Description {...qaAttribute(`${qaAttributePrefix}-description`)}>
                {description}
            </Description>

            <ButtonsContainer>
                {!resultMode && (
                    <ActionButton
                        display="secondary"
                        label={extraButtonText}
                        onClick={onExtraButtonClick}
                        {...qaAttribute(`${qaAttributePrefix}-action-button`)}
                        disabled={extraButtonDisabled}
                    />
                )}
                <ActionButton
                    display="primary"
                    label={buttonText}
                    onClick={onButtonClick}
                    {...qaAttribute(`${qaAttributePrefix}-action-button`)}
                    disabled={buttonDisabled}
                />
            </ButtonsContainer>
        </StyledSubmitResultDialog>
    );
};

export default SubmitResultDialog;
